import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Divider, Button, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemButton, ListItemText,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AddCommentIcon from '@mui/icons-material/AddComment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ChatSidebar from '../components/chat/ChatSidebar';
import NotificationList from '../components/chat/NotificationList';
import RequestList from '../components/chat/RequestList';
import ChatList from '../components/chat/ChatList';
import ChatWindow from '../components/chat/ChatWindow';
import StartChatDialog from '../components/chat/StartChatDialog';
import '../styles/Communications.css';
import { useNavigate } from 'react-router-dom';
import { getJSON, postJSON } from '../utils/api';
import { notificationPath } from '../utils/navigationUtils';
import { useChatContext } from '../context/ChatContext';

const POLL_MS = 20000; // 20s polling for simplicity

export default function Communications() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('notifications'); // 'notifications' | 'requests' | 'chats'
  const { fetchUnreadCount, resetUnreadCount } = useChatContext();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const loadNotifications = useCallback(async () => {
    try {
      const data = await getJSON('/api/notifications');
      setNotifications(data || []);
    } catch (e) { /* surface only on explicit view */ }
  }, []);

  const markNotificationRead = useCallback(async (id) => {
    try {
      await postJSON(`/api/notifications/${id}/read`, {});
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Open a notification at its source. Marking it read is deliberately not
  // awaited: the user has seen it by definition, and making the navigation
  // wait on a receipt write would stall the click for no benefit. A failed
  // write just leaves the row unread, which the next poll corrects.
  const openNotification = useCallback((n) => {
    const path = notificationPath(n?.target);
    if (!path) return;
    if (!n.read) markNotificationRead(n.id);
    navigate(path);
  }, [navigate, markNotificationRead]);

  // Requests (invitations) state — both incoming (collaborator view) and outgoing (author view)
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);

  const loadInvitations = useCallback(async () => {
    try {
      setInvitationsLoading(true);
      const [inData, outData] = await Promise.all([
        getJSON('/api/invitations/incoming'),
        getJSON('/api/invitations/outgoing'),
      ]);
      setIncoming(inData || []);
      setOutgoing(outData || []);
    } catch (e) { /* silent */ }
    finally { setInvitationsLoading(false); }
  }, []);

  const handleAcceptInvitation = useCallback(async (id) => {
    await postJSON(`/api/invitations/${id}/accept`, {});
    setIncoming(prev => prev.map(inv => inv.id === id ? { ...inv, status: 'accepted' } : inv));
  }, []);

  const handleRejectInvitation = useCallback(async (id) => {
    await postJSON(`/api/invitations/${id}/reject`, {});
    setIncoming(prev => prev.map(inv => inv.id === id ? { ...inv, status: 'rejected' } : inv));
  }, []);

  const handleCancelInvitation = useCallback(async (id) => {
    await postJSON(`/api/invitations/${id}/cancel`, {});
    setOutgoing(prev => prev.filter(inv => inv.id !== id));
  }, []);

  // Cover offer accept flow: user picks a draft to assign the cover to
  const [coverOfferInv, setCoverOfferInv] = useState(null); // currently accepting invitation
  const [userDrafts, setUserDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);

  const handleAcceptCoverOffer = useCallback(async (inv) => {
    setCoverOfferInv(inv);
    setDraftsLoading(true);
    try {
      const data = await getJSON('/api/drafts');
      // Filter to only non-cover-only drafts (manuscripts)
      const manuscripts = (data || []).filter(d => d.type !== 'cover_only');
      setUserDrafts(manuscripts);
    } catch { setUserDrafts([]); }
    finally { setDraftsLoading(false); }
  }, []);

  const handleConfirmCoverOfferDraft = useCallback(async (targetDraftId) => {
    if (!coverOfferInv) return;
    try {
      await postJSON(`/api/invitations/${coverOfferInv.id}/accept`, { targetDraftId });
      setIncoming(prev => prev.map(inv =>
        inv.id === coverOfferInv.id ? { ...inv, status: 'accepted' } : inv
      ));
      setCoverOfferInv(null);
    } catch (e) {
      alert(e.message || 'Failed to accept cover offer.');
    }
  }, [coverOfferInv]);

  // Chats state
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [startDialogOpen, setStartDialogOpen] = useState(false);

  const loadChats = useCallback(async () => {
    try {
      const data = await getJSON('/api/chats');
      setConversations(data || []);
      if (data?.length && !selectedConv) {
        setSelectedConv(data[0]);
      }
    } catch (e) { /* silent */ }
  }, [selectedConv]);

  // Initial and polling loads
  useEffect(() => {
    if (activeTab === 'notifications') {
      loadNotifications();
      const t = setInterval(loadNotifications, POLL_MS);
      return () => clearInterval(t);
    }
  }, [activeTab, loadNotifications]);

  useEffect(() => {
    if (activeTab === 'requests') {
      loadInvitations();
      const t = setInterval(loadInvitations, POLL_MS);
      return () => clearInterval(t);
    }
  }, [activeTab, loadInvitations]);

  useEffect(() => {
    if (activeTab === 'chats') {
      loadChats();
      const t = setInterval(loadChats, POLL_MS);
      return () => clearInterval(t);
    }
  }, [activeTab, loadChats]);

  // Top-level loading splash (first mount)
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        await Promise.all([loadNotifications(), loadChats(), loadInvitations()]);
        // eslint-disable-next-line no-empty
        
      } catch (e) {
        if (alive) setErr('Failed to load communications.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line

  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);
  const pendingRequestsCount = useMemo(
    () => incoming.filter(i => i.status === 'pending').length,
    [incoming]
  );

  // Reset unread count when switching to chats tab
  useEffect(() => {
    if (activeTab === 'chats') {
      resetUnreadCount();
    }
  }, [activeTab, resetUnreadCount]);

  // Refresh unread count when leaving the page
  useEffect(() => {
    return () => {
      fetchUnreadCount();
    };
  }, [fetchUnreadCount]);

  const renderContent = () => {
    switch (activeTab) {
      case 'notifications':
        return (
          <NotificationList
            items={notifications}
            onMarkRead={markNotificationRead}
            onRefresh={loadNotifications}
            onOpen={openNotification}
          />
        );
      case 'requests':
        return (
          <RequestList
            incoming={incoming}
            outgoing={outgoing}
            onAccept={handleAcceptInvitation}
            onReject={handleRejectInvitation}
            onCancel={handleCancelInvitation}
            onAcceptCoverOffer={handleAcceptCoverOffer}
            onRefresh={loadInvitations}
            loading={invitationsLoading}
          />
        );
      case 'chats':
        return (
          <Box className="comm-chat-area">
            <Box className="comm-chatlist">
              <ChatList
                conversations={conversations}
                selectedId={selectedConv?.id || null}
                onSelect={(c) => setSelectedConv(c)}
                onRefresh={loadChats}
              />
            </Box>
            <Box 
              className="comm-chatwindow"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden'
              }}
            >
              {selectedConv ? (
                <ChatWindow conversation={selectedConv} />
              ) : (
                <Box className="comm-center" p={2}>
                  <Typography variant="body2" color="text.secondary">
                    Select a conversation or start a new one.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box className="comm-root">
      <Box className="comm-container">
        <ChatSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          unreadNotifications={unreadCount}
          pendingRequests={pendingRequestsCount}
        />

        <Box 
          className="comm-main"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden'
          }}
        >
          <Box className="comm-header">
            {activeTab === 'notifications' ? (
              <>
                <NotificationsIcon fontSize="small" />
                <Typography variant="h6" ml={1}>Notifications</Typography>
              </>
            ) : activeTab === 'requests' ? (
              <>
                <PersonAddIcon fontSize="small" />
                <Typography variant="h6" ml={1}>Collaboration Requests</Typography>
              </>
            ) : (
              <>
                <ChatIcon fontSize="small" />
                <Typography variant="h6" ml={1}>Chats</Typography>
                <Button
                  size="small"
                  startIcon={<AddCommentIcon />}
                  className="comm-start-chat-btn"
                  onClick={() => setStartDialogOpen(true)}
                >
                  Start Chat
                </Button>
              </>
            )}
          </Box>
          <Divider />

          {loading ? (
            <Box className="comm-center">
              <CircularProgress />
            </Box>
          ) : err ? (
            <Box p={2}><Alert severity="error">{err}</Alert></Box>
          ) : (
            renderContent()
          )}
        </Box>
      </Box>

      <StartChatDialog
        open={startDialogOpen}
        onClose={() => setStartDialogOpen(false)}
        onCreated={(conv) => {
          setStartDialogOpen(false);
          setConversations(prev => {
            const exists = prev.find(c => c.id === conv.id);
            const updated = exists ? prev.map(c => c.id === conv.id ? conv : c) : [conv, ...prev];
            return updated;
          });
          setSelectedConv(conv);
          setActiveTab('chats');
        }}
      />

      {/* Cover Offer — Pick a Draft Dialog */}
      <Dialog
        open={!!coverOfferInv}
        onClose={() => setCoverOfferInv(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#7c3aed', color: 'white' }}>
          📦 Assign Cover to a Draft
        </DialogTitle>
        <DialogContent sx={{ pt: '1rem !important' }}>
          {coverOfferInv && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Cover <strong>"{coverOfferInv.draftTitle}"</strong> from <strong>{coverOfferInv.fromUsername}</strong>.
              Select which draft to assign it to:
            </Typography>
          )}
          {draftsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : userDrafts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              You have no manuscript drafts to assign a cover to.
            </Typography>
          ) : (
            <List disablePadding>
              {userDrafts.map(d => (
                <ListItem key={d._id || d.id} disablePadding>
                  <ListItemButton onClick={() => handleConfirmCoverOfferDraft(d._id || d.id)}>
                    <ListItemText
                      primary={d.title || 'Untitled'}
                      secondary={d.status || 'draft'}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoverOfferInv(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
